import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';

export default function ProblemSolutionSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'cards';
    const problemTitle = content.problem_title || 'Sin nosotros...';
    const solutionTitle = content.solution_title || 'Con nosotros...';

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Cards variant ──
    if (variant === 'cards') return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item, i) => (
                        <div key={i} className={`rounded-xl overflow-hidden border transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ borderColor: hexToRgba(s.accent, 0.1), transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                            <div className="p-5 border-b" style={{ borderColor: hexToRgba('#ef4444', 0.15), backgroundColor: hexToRgba('#ef4444', 0.04) }}>
                                <div className="flex items-start gap-3">
                                    <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold bg-red-500">✕</span>
                                    <p className="text-sm leading-relaxed" style={{ color: s.text }}>{item.problem}</p>
                                </div>
                            </div>
                            <div className="flex justify-center py-2">
                                <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                            </div>
                            <div className="p-5" style={{ backgroundColor: hexToRgba('#10b981', 0.04) }}>
                                <div className="flex items-start gap-3">
                                    <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold bg-emerald-500">✓</span>
                                    <p className="text-sm leading-relaxed font-medium" style={{ color: s.text }}>{item.solution}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );

    // ── Split variant ──
    if (variant === 'split') return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="rounded-xl p-6" style={{ backgroundColor: hexToRgba('#ef4444', 0.06) }}>
                        <h3 className="text-lg font-bold mb-4 text-red-800">{problemTitle}</h3>
                        <ul className="space-y-4">
                            {items.map((item, i) => (
                                <li key={i} className={`flex items-start gap-3 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] mt-0.5 bg-red-500">✕</span>
                                    <span className="text-sm" style={{ color: s.text }}>{item.problem}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-xl p-6" style={{ backgroundColor: hexToRgba('#10b981', 0.06) }}>
                        <h3 className="text-lg font-bold mb-4 text-emerald-800">{solutionTitle}</h3>
                        <ul className="space-y-4">
                            {items.map((item, i) => (
                                <li key={i} className={`flex items-start gap-3 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                                    style={{ transitionDelay: visible ? `${i * 100 + 200}ms` : '0ms' }}>
                                    <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] mt-0.5 bg-emerald-500">✓</span>
                                    <span className="text-sm font-medium" style={{ color: s.text }}>{item.solution}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );

    // ── Before-after variant ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="space-y-4">
                    {items.map((item, i) => (
                        <div key={i} className={`flex flex-col sm:flex-row items-center gap-3 sm:gap-5 p-4 rounded-lg transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ backgroundColor: hexToRgba(s.accent, 0.03), transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                            <div className="flex-1 flex items-center gap-2">
                                {item.icon && <SectionIcon name={item.icon} size={18} />}
                                <span className="line-through opacity-50 text-sm" style={{ color: s.text }}>{item.problem}</span>
                            </div>
                            <svg className="w-5 h-5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                            <div className="flex-1">
                                <span className="font-semibold text-sm" style={{ color: s.accent }}>{item.solution}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
